import React, { useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import classNames from "classnames";
// MUI Components
import AppBar from "@material-ui/core/AppBar";
import Menu from "@material-ui/core/Menu";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import Button from "@material-ui/core/Button";
import ListItem from "@material-ui/core/ListItem";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import Card from "@material-ui/core/Card";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import Slider from "@material-ui/core/Slider";

// Components
import LocationsList from "./LocationsList";
import BodyMap from "./BodyMap";

import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";
import {
  locationTriangleOffsetPixels,
  customFaceMapId,
  faceMapId,
  heatmapTmodeType,
  heatmapEmodeType,
} from "../../constants/constants";

// Icons
import Switch from "@material-ui/core/Switch";
import EditIcon from "@material-ui/icons/Edit";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import CameraModal from "../AcquireBar/CameraModal";

// Actions
import {
  setLocationViewStyle,
  setSelectorPosition,
  toggleShowCompleted,
  setMainViewSection,
  toggleEditMapMode,
  toggleRemoveLocationMode,
  toggleHeatmap,
  setCameraModal,
  enterAddNewLocation,
  setHeatmapType,
  setHeatmapLimit,
} from "../../actions";

function LocationSection(props) {
  const useStyles = makeStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      position: "relative",
      minHeight: 0,
      maxWidth: "100%",
    },
    appBar: {
      boxShadow: "unset",
      flexDirection: "row",
      backgroundColor: colors[props.theme].locationListHeader,
      zIndex: "900",
      justifyContent: "space-between",
    },
    overviewButton: {
      padding: "0px 0px 0px 23px",
    },
    tabContainer: {
      display: "flex",
      width: "50%",
    },
    tabSelected: {
      backgroundColor: colors[props.theme].locationListTabSelected,
      marginBottom: "0px",
      height: "30px",
      boxShadow:
        props.theme === "light"
          ? `${colors[props.theme].boxShadowColor}, 0.2) 0px -3px 3px 1px`
          : `${colors[props.theme].boxShadowColor}, 0.2) 0px -3px 2px 0px`,
      padding: "0px",
      width: "50%",
      alignSelf: "flex-end",
      borderRadius: "10px 10px 0px 0px",
      marginLeft: "1px",
      "&:hover": {
        backgroundColor: colors[props.theme].bkg1,
      },
    },
    tabUnselected: {
      backgroundColor: colors[props.theme].locationListTabUnselected,
      marginBottom: "2px",
      height: "28px",
      boxShadow: "unset",
      padding: "0px",
      width: "50%",
      alignSelf: "flex-end",
      borderRadius: "10px 10px 0px 0px",
      marginLeft: "1px",
      "&:hover": {
        backgroundColor: colors[props.theme].bkg1,
      },
    },
    swipeableViews: {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
    },
    triangle: {
      borderColor: `transparent ${
        colors[props.theme].bkg2
      } transparent transparent`,
      top: props.selectorPosition,
      ...customTheme().triangle,
    },
    arrowIcon: {
      ...customTheme().iconMedium,
      backgroundColor: colors[props.theme].locationListHeader,
    },
  });
  const classes = useStyles();
  const triangleSelector = useRef(null);
  const hiddenPosition = -20;

  useEffect(() => {
    // dont want this to run in BODY_MAP mode
    if (props.currentLocationView === "LIST") {
      // reset triangle crop
      triangleSelector.current.style.clipPath = "inset(0px 0 0 0)";
    } else if (props.currentLocationView === "BODY_MAP" || props.leftView) {
      props.dispatch(setSelectorPosition(hiddenPosition));
    }
  });

  const handleOverview = () => {
    if (props.currentLocationView !== "LIST") {
      props.dispatch(setLocationViewStyle("LIST"));
    }
    props.dispatch(setMainViewSection("mapFocused"));
    // hide triangle selector
    props.dispatch(setSelectorPosition(hiddenPosition));
  };

  // Change current view by clicking on tab
  const handleChange = (viewId) => {
    if (!props.editMapMode && !props.addNewLocationMode) {
      // don't watch to switch between tabs while editing/adding new location
      props.dispatch(setLocationViewStyle(viewId));
      if (viewId === "BODY_MAP" || props.leftView) {
        props.dispatch(setSelectorPosition(hiddenPosition)); // hide selector
      }
    }
  };
  // Update scroll distance when scrolling
  // moves selector triangle with scroll without ease animation (via neg margin)
  // not using state/redux here because it causes a rerender and is a performance nightmare
  const handleScroll = (scrollTop) => {
    // const scrollTop = e.target.scrollTop;
    // console.log("handling scroll!", scrollTop);
    // requestAnimationFrame should throttle DOM updates so they cant exceed browser framerate
    requestAnimationFrame(() => {
      const selector = triangleSelector.current;

      if (selector) {
        // changing negative marginTop will move selector while user scrolls without animating it
        selector.style.marginTop = `-${scrollTop}px`;

        // distance between triangle selector and the 'show completed' bar
        // "locationTriangleOffsetPixels" is pixel height of location tabs + 'show completed' bar
        const triangleToTop =
          props.selectorPosition -
          locationTriangleOffsetPixels -
          selector.offsetHeight / 2;

        // console.log("scrollTop", scrollTop, "triangleToTop", triangleToTop);
        // mask or 'clip' portion of the triangle selector that appears above the 'show completed' bar
        selector.style.clipPath = `inset(${scrollTop - triangleToTop}px 0 0 0)`;
      }
    });
  };

  return (
    <div className={classes.root}>
      <AppBar position="static" color="default" className={classes.appBar}>
        <IconButton
          disableRipple
          disabled={props.editMode || props.addNewLocationMode}
          onClick={() => handleOverview()}
          className={classes.overviewButton}
        >
          <KeyboardArrowLeft className={classes.arrowIcon} />
        </IconButton>
        <div className={classes.tabContainer}>
          <Button
            disabled={props.editMapMode || props.addNewLocationMode}
            disableRipple
            className={
              props.currentLocationView === "LIST"
                ? classes.tabSelected
                : classes.tabUnselected
            }
            onClick={() => handleChange("LIST")}
          >
            List
          </Button>

          <Button
            disabled={props.removeLocationMode || props.addNewLocationMode}
            disableRipple
            className={
              props.currentLocationView === "BODY_MAP"
                ? classes.tabSelected
                : classes.tabUnselected
            }
            onClick={() => handleChange("BODY_MAP")}
          >
            Map
          </Button>
        </div>
        <LocationsFilter
          showCompleted={props.showCompleted}
          maps={props.maps}
          allIds={props.allIds}
          acquiring={props.acquiring}
          disableEditMenu={props.disableEditMenu}
          dispatch={props.dispatch}
          heatmap={props.heatmap}
          editMapMode={props.editMapMode}
          removeLocationMode={props.removeLocationMode}
          editMode={props.editMode}
          currentLocationView={props.currentLocationView}
          connectionEstablished={props.connectionEstablished}
          faceMapIncluded={props.faceMapIncluded}
          cameraModalStatus={props.cameraModalStatus}
          theme={props.theme}
          setHeatmapType={props.setHeatmapType}
          setHeatmapLimit={props.setHeatmapLimit}
        />
      </AppBar>
      {props.currentLocationView === "LIST" ? (
        <LocationsList handleScroll={(e) => handleScroll(e)} />
      ) : (
        <BodyMap />
      )}
      <div className={classes.triangle} ref={triangleSelector} />
    </div>
  );
}

const LocationsFilter = React.memo(function LocationsFilter(props) {
  const switchStyles = makeStyles({
    root: {
      backgroundColor: colors[props.theme].locationListHeader, // colors.headerBkg,
      flex: "0 0 auto",
      display: "flex",
      flexDirection: "row",
      height: "35px",
      alignItems: "center",
      justifyContent: "center",
    },
    editButton: {
      padding: "0px 23px 0px 0px",
    },
    editButtonWiggle: {
      padding: "0px 23px 0px 0px",
      animationName: "editIconAnimation",
      animationIterationCount: "infinite",
      transformOrigin: "50% 10%",
      animationDelay: "-.2s",
      animationDuration: ".4s",
    },
    listItem: {
      paddingTop: "0px",
      paddingBottom: "0px",
      width: "250px",
      minHeight: "40px",
    },
    heatmapTypeItem: {
      paddingLeft: "20px",
      "& .MuiFormControlLabel-label": {
        fontSize: "0.875rem",
      },
    },
    collapse: {
      boxShadow: "unset",
    },
    dialogRoot: customTheme(props.theme).dialog.root,
    dialogTitle: customTheme().dialog.title,
    dialogContentContainer: customTheme().dialog.contentContainer,
    dialogButtonContainer: customTheme().dialog.buttonContainer,
    dialogButtonPrimary: customTheme(props.theme).buttonPrimary,
    dialogButtonOutlined: customTheme(props.theme).buttonOutlined,
    sliderDiv: {
      width: "100%",
      marginBottom: "15px",
    },
    sliderContainer: {
      margin: "25px 20px 40px 20px",
      width: "90%",
    },
  });

  const classes = switchStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [heatmapTypeCollapse, setHeatmapTypeCollapse] = React.useState(
    props.heatmap.status
  );

  const [heatmapLimits, changeHeatmapLimits] = React.useState({
    heatmapLimitDialog: false,
    swsLimit: [props.heatmap.swsLimit[0], props.heatmap.swsLimit[1]],
    thicknessLimit: [
      props.heatmap.thicknessLimit[0],
      props.heatmap.thicknessLimit[1],
    ],
  });

  const handleEditClick = (event) => {
    if (props.editMode) {
      if (props.editMapMode) {
        props.dispatch(toggleEditMapMode());
      } else if (props.removeLocationMode) {
        props.dispatch(toggleRemoveLocationMode());
      }
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleEditMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangeHeatmapLimit = () => {
    props.dispatch(
      setHeatmapLimit({
        swsLimit: heatmapLimits.swsLimit,
        thicknessLimit: heatmapLimits.thicknessLimit,
      })
    );
    changeHeatmapLimits({ ...heatmapLimits, heatmapLimitDialog: false });
  };

  return (
    <div className={classes.root}>
      {/* EDIT BUTTON */}
      <IconButton
        disableRipple
        className={
          props.editMode ? classes.editButtonWiggle : classes.editButton
        }
        onClick={handleEditClick}
        disabled={props.disableEditMenu}
      >
        <EditIcon />
      </IconButton>

      {/* LOCATION LIST MENU */}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleEditMenuClose}
      >
        {props.currentLocationView === "LIST" && (
          <ListItem ContainerComponent="div" className={classes.listItem}>
            <ListItemText primary="Show Completed" />
            <ListItemSecondaryAction>
              <Switch
                onChange={() => {
                  handleEditMenuClose();
                  props.dispatch(toggleShowCompleted());
                }}
                checked={props.showCompleted}
                color="primary"
              />
            </ListItemSecondaryAction>
          </ListItem>
        )}

        {props.currentLocationView === "BODY_MAP" && (
          <ListItem ContainerComponent="div" className={classes.listItem}>
            <ListItemText primary="Heat Map" />
            <ListItemSecondaryAction>
              <Switch
                disabled={props.editMapMode}
                onChange={() => {
                  if (props.heatmap.status) {
                    setHeatmapTypeCollapse(false);
                  } else {
                    setHeatmapTypeCollapse(true);
                  }
                  props.dispatch(toggleHeatmap());
                }}
                checked={props.heatmap.status}
                color="primary"
              />
            </ListItemSecondaryAction>
          </ListItem>
        )}
        {props.currentLocationView === "BODY_MAP" && (
          <Collapse in={heatmapTypeCollapse} timeout="auto" unmountOnExit>
            <Card className={classes.collapse}>
              <RadioGroup
                value={props.heatmap.type}
                onChange={(event) =>
                  props.dispatch(setHeatmapType(event.target.value))
                }
              >
                <FormControlLabel
                  value={heatmapEmodeType}
                  className={classes.heatmapTypeItem}
                  control={<Radio size="small" color="primary" />}
                  label="E-Mode"
                />
                <FormControlLabel
                  value={heatmapTmodeType}
                  className={classes.heatmapTypeItem}
                  control={<Radio size="small" color="primary" />}
                  label="T-Mode"
                />
              </RadioGroup>
            </Card>
          </Collapse>
        )}
        {props.currentLocationView === "BODY_MAP" && (
          <ListItem
            button
            onClick={() => {
              handleEditMenuClose();
              changeHeatmapLimits({
                ...heatmapLimits,
                heatmapLimitDialog: true,
              });
            }}
            className={classes.listItem}
          >
            <ListItemText primary="Change Heatmap Limits" />
          </ListItem>
        )}

        {props.currentView === "BODY_MAP" && (
          <ListItem
            button
            onClick={() => {
              handleEditMenuClose();
              props.dispatch(toggleEditMapMode());
            }}
            className={classes.listItem}
          >
            <ListItemText primary="Edit Location Position" />
          </ListItem>
        )}

        <ListItem
          button
          className={classes.listItem}
          onClick={() => {
            handleEditMenuClose();
            props.dispatch(enterAddNewLocation(Object.keys(props.maps)));
          }}
        >
          <ListItemText primary="Add New Location" />
        </ListItem>
        <ListItem
          button
          onClick={() => {
            handleEditMenuClose();
            props.dispatch(toggleRemoveLocationMode());
            if (props.currentLocationView === "BODY_MAP") {
              props.dispatch(setLocationViewStyle("LIST"));
            }
          }}
        >
          <ListItemText primary="Remove/Restore Location" />
        </ListItem>

        {props.faceMapIncluded && (
          // Only display this option IF the protocol has face maps included
          <ListItem
            button
            onClick={() => {
              handleEditMenuClose();
              props.dispatch(setCameraModal(true, null));
            }}
            className={classes.listItem}
          >
            <ListItemText primary="Customize Face Map" />
          </ListItem>
        )}
      </Menu>
      {heatmapLimits.heatmapLimitDialog && (
        <Dialog
          open={heatmapLimits.heatmapLimitDialog}
          PaperProps={{ className: classNames(classes.dialogRoot) }}
        >
          <Typography
            className={classes.dialogTitle}
            variant="h4"
            color="textPrimary"
            align="center"
          >
            Change Heatmap Limits
          </Typography>
          <DialogContent className={classes.dialogContentContainer}>
            <div className={classes.sliderDiv}>
              <Typography gutterBottom>E-Mode Limit</Typography>

              <Slider
                value={heatmapLimits.swsLimit}
                step={0.5}
                className={classes.sliderContainer}
                color={"primary"}
                aria-labelledby="discrete-slider-always"
                min={0}
                max={10}
                valueLabelFormat={(value) => `${value}m/s`}
                marks={[
                  {
                    value: 0,
                    label: "0 m/s",
                  },
                  {
                    value: 2,
                    label: "2 m/s",
                  },
                  {
                    value: 4,
                    label: "4 m/s",
                  },
                  {
                    value: 6,
                    label: "6 m/s",
                  },
                  {
                    value: 8,
                    label: "8 m/s",
                  },
                  {
                    value: 10,
                    label: "10 m/s",
                  },
                ]}
                valueLabelDisplay="on"
                onChange={(e, newValue) =>
                  changeHeatmapLimits({ ...heatmapLimits, swsLimit: newValue })
                }
              />
            </div>
            <div className={classes.sliderDiv}>
              <Typography gutterBottom>T-Mode Limit</Typography>

              <Slider
                value={heatmapLimits.thicknessLimit}
                step={0.5}
                className={classes.sliderContainer}
                color={"primary"}
                aria-labelledby="discrete-slider-always"
                min={0}
                max={6}
                valueLabelFormat={(value) => `${value}mm`}
                marks={[
                  {
                    value: 0,
                    label: "0 mm",
                  },
                  {
                    value: 1,
                    label: "1 mm",
                  },
                  {
                    value: 2,
                    label: "2 mm",
                  },
                  {
                    value: 3,
                    label: "3 mm",
                  },
                  {
                    value: 4,
                    label: "4 mm",
                  },
                  {
                    value: 5,
                    label: "5 mm",
                  },
                  {
                    value: 6,
                    label: "6 mm",
                  },
                ]}
                valueLabelDisplay="on"
                onChange={(e, newValue) =>
                  changeHeatmapLimits({
                    ...heatmapLimits,
                    thicknessLimit: newValue,
                  })
                }
              />
            </div>
          </DialogContent>

          <DialogActions className={classes.dialogButtonContainer}>
            <Button
              onClick={() =>
                changeHeatmapLimits({
                  swsLimit: [
                    props.heatmap.swsLimit[0],
                    props.heatmap.swsLimit[1],
                  ],
                  thicknessLimit: [
                    props.heatmap.thicknessLimit[0],
                    props.heatmap.thicknessLimit[1],
                  ],
                  heatmapLimitDialog: false,
                })
              }
              variant="outlined"
              className={classes.dialogButtonOutlined}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleChangeHeatmapLimit()}
              className={classes.dialogButtonPrimary}
              variant="contained"
              color="primary"
              disabled={
                heatmapLimits.swsLimit[0] === heatmapLimits.swsLimit[1] ||
                heatmapLimits.thicknessLimit[0] ===
                  heatmapLimits.thicknessLimit[1]
              }
            >
              Apply Limits
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* The acquire bar already has the CameraModal so the Camera Modal only needs to me mounted here if the acquire bar is not rendered */}
      {!props.connectionEstablished && props.cameraModalStatus ? (
        <CameraModal />
      ) : null}
    </div>
  );
});

const mapStateToProps = (state) => {
  return {
    currentLocationView: state.locations.currentLocationView,
    editMapMode: state.utility.mode.editMapMode,
    heatmap: state.utility.heatmap,
    removeLocationMode: state.utility.mode.removeLocationMode,
    editMode:
      state.utility.mode.editMapMode || state.utility.mode.removeLocationMode, //boolean
    addNewLocationMode: state.utility.mode.addNewLocationMode,
    selectorPosition: state.locations.selectorPosition,
    allIds: state.locations.allIds,
    maps: state.protocol.maps,
    currentMapId: state.locations.currentMapId,
    disableEditMenu:
      !state.utility.samplesDone ||
      state.device.acquiring ||
      state.utility.mode.addNewLocationMode, //boolean
    connectionEstablished: state.device.connectionEstablished,
    cameraModalStatus: state.utility.cameraModal.status,
    leftView: state.view.mainViewSection.orientation === "LEFT", //boolean
    showCompleted: state.utility.showCompleted,
    acqType: state.locations.currentAcqType,
    faceMapIncluded:
      Object.keys(state.protocol.maps).includes(faceMapId) ||
      Object.keys(state.protocol.maps).includes(customFaceMapId),
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps)(LocationSection);
